import { useTranslation } from 'react-i18next';
import { GateSelectorProps } from './props';
import { buildParkingGateOptions } from '../../../utils/utilGates';
import { useAuth } from '../../../lib/auth';
import { useParkingBookingSlice } from '../../../slices/useParkingBookingSlice';

export const GateSelector = ({
  parking,
  selectedGate,
  onSelectGate,
}: GateSelectorProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { userBooking } = useParkingBookingSlice();

  const gates = buildParkingGateOptions(parking, currentUser, userBooking);
  const anyGateHasUserSession = gates.some((g) => g.hasActiveUserSession);
  const anyGateHasUserBooking = gates.some((g) => g.hasBookingInFuture);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full justify-between items-center">
        <div className="text-xs font-bold font-mono">{t('selectGate')}</div>
      </div>
      <div className="flex flex-row gap-2 overflow-x-scroll no-scrollbar pb-2">
        {gates.map((gate) => {
          const isSelected = gate.value === selectedGate?.value;
          const isGrayedOut =
            (anyGateHasUserSession &&
              !gate.hasActiveUserSession &&
              !gate.hasBookingInFuture) ||
            (anyGateHasUserBooking &&
              !gate.hasBookingInFuture &&
              !gate.hasActiveUserSession);
          const isDisabled = !gate.canUnlock || isGrayedOut;

          const buttonContent = (
            <>
              <span className="font-mono font-bold text-sm leading-tight">
                {gate.label}
              </span>
              {gate.hasActiveUserSession && (
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${isSelected ? 'text-white' : 'text-primary'}`}
                >
                  {t('yourSession')}
                </span>
              )}
              {gate.hasBookingInFuture && !gate.hasActiveUserSession && (
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${isSelected ? 'text-white' : 'text-primary'}`}
                >
                  {t('yourBooking')}
                </span>
              )}
              {(gate.hasOtherUserSession || gate.hasOtherUserOngoingBooking) && (
                <span className="text-[10px] mt-0.5 font-medium leading-tight text-danger">
                  {t('occupied')}
                </span>
              )}
            </>
          );

          if (isSelected) {
            return (
              <button
                key={gate.value}
                disabled={isDisabled}
                onClick={() => onSelectGate(gate)}
                style={
                  { '--fill': 'var(--color-primary)' } as React.CSSProperties
                }
                className={`gradient-border-spin flex flex-col justify-center items-center rounded-lg px-4 py-3 min-w-[88px] shrink-0 transition-all text-white
                  ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}
                `}
              >
                {buttonContent}
              </button>
            );
          }

          if (gate.hasOtherUserSession || gate.hasOtherUserOngoingBooking) {
            return (
              <button
                key={gate.value}
                disabled
                onClick={() => onSelectGate(gate)}
                className="flex flex-col justify-center items-center rounded-lg px-4 py-3 min-w-[88px] shrink-0 bg-secondary border-2 border-danger cursor-not-allowed text-black"
              >
                {buttonContent}
              </button>
            );
          }

          if (gate.hasActiveUserSession) {
            return (
              <button
                key={gate.value}
                disabled={isDisabled}
                onClick={() => onSelectGate(gate)}
                className={`gradient-border-spin flex flex-col justify-center items-center rounded-lg px-4 py-3 min-w-[88px] shrink-0 transition-all text-black
                  ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-primary hover:text-white'}
                `}
              >
                {buttonContent}
              </button>
            );
          }

          return (
            <button
              key={gate.value}
              disabled={isDisabled}
              onClick={() => onSelectGate(gate)}
              className={`flex flex-col justify-center items-center rounded-lg px-4 py-3 min-w-[88px] shrink-0 transition-all bg-secondary text-black
                ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-primary hover:text-white'}
                ${gate.hasBookingInFuture ? 'border-2 border-primary' : ''}
              `}
            >
              {buttonContent}
            </button>
          );
        })}
      </div>
    </div>
  );
};
